"use client"

import React, { useRef, useState, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { createPortal } from 'react-dom'
import {
    X, Minus, Maximize2, Minimize2,
    Pen, Eraser, Trash2, Download,
    Square, Circle, Minus as LineIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'

// ─── Types ──────────────────────────────────────────────────────────────────
type ResizeDir = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'
const CURSOR_MAP: Record<ResizeDir, string> = {
    n: 'ns-resize', s: 'ns-resize',
    e: 'ew-resize', w: 'ew-resize',
    ne: 'nesw-resize', sw: 'nesw-resize',
    nw: 'nwse-resize', se: 'nwse-resize',
}
const MIN_W = 320
const MIN_H = 220

interface Point { x: number; y: number }
type DrawMode = 'pen' | 'eraser' | 'line' | 'rect' | 'circle'

const COLORS = [
    '#1e1e1e', '#ef4444', '#f97316', '#eab308',
    '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899',
    '#ffffff',
]
const BRUSH_SIZES = [2, 4, 8, 14, 24]

interface WhiteboardModalProps {
    open: boolean
    onClose: () => void
}

export function WhiteboardModal({ open, onClose }: WhiteboardModalProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const snapshotRef = useRef<ImageData | null>(null)
    const [mode, setMode] = useState<DrawMode>('pen')
    const [color, setColor] = useState('#1e1e1e')
    const [brushSize, setBrushSize] = useState(4)
    const [isDrawing, setIsDrawing] = useState(false)
    const [isMinimized, setIsMinimized] = useState(false)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [showColorPicker, setShowColorPicker] = useState(false)
    const [showBrushPicker, setShowBrushPicker] = useState(false)
    const lastPoint = useRef<Point | null>(null)
    const startPoint = useRef<Point | null>(null)

    // Window position & size
    const [pos, setPos] = useState({ x: 80, y: 80 })
    const [size, setSize] = useState({ w: 760, h: 520 })

    // Drag state
    const dragging = useRef(false)
    const dragStart = useRef({ mx: 0, my: 0, ox: 0, oy: 0 })

    // Resize state
    const resizing = useRef<ResizeDir | null>(null)
    const resizeStart = useRef({ mx: 0, my: 0, ox: 0, oy: 0, ow: 0, oh: 0 })

    // ── Canvas init ──────────────────────────────────────────────────────────
    useEffect(() => {
        if (!open || !canvasRef.current) return
        const canvas = canvasRef.current
        canvas.width = canvas.offsetWidth
        canvas.height = canvas.offsetHeight
        const ctx = canvas.getContext('2d')!
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
    }, [open])

    useEffect(() => {
        if (!canvasRef.current || !open) return
        const ro = new ResizeObserver(() => {
            const canvas = canvasRef.current!
            const imageData = canvas.getContext('2d')?.getImageData(0, 0, canvas.width, canvas.height)
            canvas.width = canvas.offsetWidth
            canvas.height = canvas.offsetHeight
            const ctx = canvas.getContext('2d')!
            ctx.fillStyle = '#ffffff'
            ctx.fillRect(0, 0, canvas.width, canvas.height)
            if (imageData) ctx.putImageData(imageData, 0, 0)
        })
        ro.observe(canvasRef.current)
        return () => ro.disconnect()
    }, [open])

    // ── Drawing helpers ──────────────────────────────────────────────────────
    const getPos = (e: React.PointerEvent): Point => {
        const rect = canvasRef.current!.getBoundingClientRect()
        return { x: e.clientX - rect.left, y: e.clientY - rect.top }
    }

    const startDraw = (e: React.PointerEvent) => {
        const p = getPos(e)
        setIsDrawing(true)
        startPoint.current = p
        lastPoint.current = p
        if (mode !== 'pen' && mode !== 'eraser') {
            const ctx = canvasRef.current!.getContext('2d')!
            snapshotRef.current = ctx.getImageData(0, 0, canvasRef.current!.width, canvasRef.current!.height)
        }
        if (mode === 'pen' || mode === 'eraser') {
            const ctx = canvasRef.current!.getContext('2d')!
            ctx.beginPath()
            ctx.arc(p.x, p.y, brushSize / 2, 0, Math.PI * 2)
            ctx.fillStyle = mode === 'eraser' ? '#ffffff' : color
            ctx.fill()
        }
        ; (e.target as HTMLElement).setPointerCapture(e.pointerId)
    }

    const doDraw = useCallback((e: React.PointerEvent) => {
        if (!isDrawing || !canvasRef.current) return
        const ctx = canvasRef.current.getContext('2d')!
        const p = getPos(e)

        if (mode === 'pen') {
            if (!lastPoint.current) return
            ctx.lineJoin = 'round'
            ctx.lineCap = 'round'
            ctx.globalCompositeOperation = 'source-over'
            ctx.strokeStyle = color
            ctx.lineWidth = brushSize
            ctx.beginPath()
            ctx.moveTo(lastPoint.current.x, lastPoint.current.y)
            ctx.lineTo(p.x, p.y)
            ctx.stroke()
            lastPoint.current = p
        } else if (mode === 'eraser') {
            if (!lastPoint.current) return
            ctx.lineJoin = 'round'
            ctx.lineCap = 'round'
            ctx.globalCompositeOperation = 'source-over'
            ctx.strokeStyle = '#ffffff'
            ctx.lineWidth = brushSize * 3
            ctx.beginPath()
            ctx.moveTo(lastPoint.current.x, lastPoint.current.y)
            ctx.lineTo(p.x, p.y)
            ctx.stroke()
            lastPoint.current = p
        } else {
            if (snapshotRef.current) ctx.putImageData(snapshotRef.current, 0, 0)
            ctx.strokeStyle = color
            ctx.lineWidth = brushSize
            ctx.lineCap = 'round'
            ctx.globalCompositeOperation = 'source-over'
            const sp = startPoint.current!
            if (mode === 'line') {
                ctx.beginPath()
                ctx.moveTo(sp.x, sp.y)
                ctx.lineTo(p.x, p.y)
                ctx.stroke()
            } else if (mode === 'rect') {
                ctx.strokeRect(sp.x, sp.y, p.x - sp.x, p.y - sp.y)
            } else if (mode === 'circle') {
                const rx = Math.abs(p.x - sp.x) / 2
                const ry = Math.abs(p.y - sp.y) / 2
                const cx = sp.x + (p.x - sp.x) / 2
                const cy = sp.y + (p.y - sp.y) / 2
                ctx.beginPath()
                ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2)
                ctx.stroke()
            }
        }
    }, [isDrawing, mode, color, brushSize])

    const stopDraw = () => {
        setIsDrawing(false)
        lastPoint.current = null
        startPoint.current = null
        snapshotRef.current = null
    }

    const clearCanvas = () => {
        const canvas = canvasRef.current
        if (!canvas) return
        const ctx = canvas.getContext('2d')!
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
    }

    const downloadCanvas = () => {
        const canvas = canvasRef.current
        if (!canvas) return
        const link = document.createElement('a')
        link.download = `whiteboard-${Date.now()}.png`
        link.href = canvas.toDataURL()
        link.click()
    }

    // ── Drag header ──────────────────────────────────────────────────────────
    const onHeaderMouseDown = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest('button')) return
        dragging.current = true
        dragStart.current = { mx: e.clientX, my: e.clientY, ox: pos.x, oy: pos.y }
    }

    // ── Resize handles ───────────────────────────────────────────────────────
    const onResizeMouseDown = (e: React.MouseEvent, dir: ResizeDir) => {
        e.stopPropagation()
        e.preventDefault()
        resizing.current = dir
        resizeStart.current = {
            mx: e.clientX, my: e.clientY,
            ox: pos.x, oy: pos.y,
            ow: size.w, oh: size.h,
        }
    }

    // ── Global mouse move/up ─────────────────────────────────────────────────
    useEffect(() => {
        const onMove = (e: MouseEvent) => {
            if (dragging.current) {
                setPos({
                    x: dragStart.current.ox + (e.clientX - dragStart.current.mx),
                    y: dragStart.current.oy + (e.clientY - dragStart.current.my),
                })
            }
            if (resizing.current) {
                const dir = resizing.current
                const dx = e.clientX - resizeStart.current.mx
                const dy = e.clientY - resizeStart.current.my
                const { ox, oy, ow, oh } = resizeStart.current

                let newX = ox, newY = oy, newW = ow, newH = oh

                // Width
                if (dir.includes('e')) newW = Math.max(MIN_W, ow + dx)
                if (dir.includes('w')) {
                    newW = Math.max(MIN_W, ow - dx)
                    newX = ox + ow - newW
                }
                // Height
                if (dir.includes('s')) newH = Math.max(MIN_H, oh + dy)
                if (dir.includes('n')) {
                    newH = Math.max(MIN_H, oh - dy)
                    newY = oy + oh - newH
                }

                setSize({ w: newW, h: newH })
                setPos({ x: newX, y: newY })
            }
        }
        const onUp = () => {
            dragging.current = false
            resizing.current = null
        }
        window.addEventListener('mousemove', onMove)
        window.addEventListener('mouseup', onUp)
        return () => {
            window.removeEventListener('mousemove', onMove)
            window.removeEventListener('mouseup', onUp)
        }
    }, [])

    if (!open || typeof document === 'undefined') return null

    const windowStyle = isFullscreen
        ? { top: 0, left: 0, width: '100vw', height: '100vh', borderRadius: 0 }
        : {
            top: pos.y, left: pos.x,
            width: size.w,
            height: isMinimized ? 'auto' : size.h,
            minWidth: MIN_W,
            minHeight: isMinimized ? 'auto' : MIN_H,
        }

    return createPortal(
        <div
            className="fixed inset-0 z-[9995] pointer-events-none"
            aria-modal="true"
            role="dialog"
            aria-label="Whiteboard"
        >
            <div
                className={cn(
                    "absolute pointer-events-auto flex flex-col select-none",
                    "bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700",
                    "shadow-2xl overflow-visible",
                    !isFullscreen && "rounded-2xl",
                    isFullscreen && "rounded-none"
                )}
                style={windowStyle}
            >
                {/* ── Resize handles (8 directions) ── */}
                {!isFullscreen && !isMinimized && (
                    <>
                        {(['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'] as ResizeDir[]).map(dir => {
                            const isCorner = dir.length === 2
                            const styles: React.CSSProperties = {
                                position: 'absolute',
                                zIndex: 10,
                                cursor: CURSOR_MAP[dir],
                            }
                            if (dir === 'n') { styles.top = -5; styles.left = 12; styles.right = 12; styles.height = 10 }
                            if (dir === 's') { styles.bottom = -5; styles.left = 12; styles.right = 12; styles.height = 10 }
                            if (dir === 'e') { styles.right = -5; styles.top = 12; styles.bottom = 12; styles.width = 10 }
                            if (dir === 'w') { styles.left = -5; styles.top = 12; styles.bottom = 12; styles.width = 10 }
                            if (dir === 'ne') { styles.top = -6; styles.right = -6; styles.width = 14; styles.height = 14; styles.borderRadius = '0 50% 0 0' }
                            if (dir === 'nw') { styles.top = -6; styles.left = -6; styles.width = 14; styles.height = 14; styles.borderRadius = '50% 0 0 0' }
                            if (dir === 'se') { styles.bottom = -6; styles.right = -6; styles.width = 16; styles.height = 16; styles.borderRadius = '0 0 50% 0' }
                            if (dir === 'sw') { styles.bottom = -6; styles.left = -6; styles.width = 14; styles.height = 14; styles.borderRadius = '0 0 0 50%' }
                            return (
                                <div
                                    key={dir}
                                    style={styles}
                                    onMouseDown={(e) => onResizeMouseDown(e, dir)}
                                    className={cn(
                                        isCorner && dir === 'se'
                                            ? "flex items-end justify-end pb-1 pr-1"
                                            : ""
                                    )}
                                >
                                    {/* SE corner grip dots */}
                                    {dir === 'se' && (
                                        <svg width="10" height="10" viewBox="0 0 10 10" className="opacity-40">
                                            <circle cx="2" cy="8" r="1.5" fill="#94a3b8" />
                                            <circle cx="5" cy="8" r="1.5" fill="#94a3b8" />
                                            <circle cx="8" cy="8" r="1.5" fill="#94a3b8" />
                                            <circle cx="5" cy="5" r="1.5" fill="#94a3b8" />
                                            <circle cx="8" cy="5" r="1.5" fill="#94a3b8" />
                                            <circle cx="8" cy="2" r="1.5" fill="#94a3b8" />
                                        </svg>
                                    )}
                                </div>
                            )
                        })}
                    </>
                )}

                {/* ── Header ─────────────────────────────────────────────── */}
                <div
                    className="flex items-center justify-between px-4 py-2.5 bg-zinc-50 dark:bg-zinc-800/80 border-b border-zinc-200 dark:border-zinc-700 cursor-grab active:cursor-grabbing shrink-0 rounded-t-2xl"
                    onMouseDown={isFullscreen ? undefined : onHeaderMouseDown}
                >
                    <div className="flex items-center gap-2">
                        <div className="flex gap-1.5">
                            <button onClick={onClose} className="w-3 h-3 rounded-full bg-red-500 hover:bg-red-600 transition-colors flex items-center justify-center group" title="Close">
                                <X className="h-2 w-2 opacity-0 group-hover:opacity-100 text-red-800" />
                            </button>
                            <button onClick={() => setIsMinimized(m => !m)} className="w-3 h-3 rounded-full bg-yellow-400 hover:bg-yellow-500 transition-colors flex items-center justify-center group" title={isMinimized ? 'Expand' : 'Minimize'}>
                                <Minus className="h-2 w-2 opacity-0 group-hover:opacity-100 text-yellow-800" />
                            </button>
                            <button onClick={() => { setIsFullscreen(f => !f); setIsMinimized(false) }} className="w-3 h-3 rounded-full bg-green-500 hover:bg-green-600 transition-colors flex items-center justify-center group" title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
                                {isFullscreen
                                    ? <Minimize2 className="h-2 w-2 opacity-0 group-hover:opacity-100 text-green-800" />
                                    : <Maximize2 className="h-2 w-2 opacity-0 group-hover:opacity-100 text-green-800" />}
                            </button>
                        </div>
                        <span className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 ml-2 tracking-wide">Whiteboard</span>
                    </div>

                    {!isMinimized && (
                        <div className="flex items-center gap-1">
                            {([
                                { m: 'pen' as DrawMode, Icon: Pen, label: 'Pen' },
                                { m: 'line' as DrawMode, Icon: LineIcon, label: 'Line' },
                                { m: 'rect' as DrawMode, Icon: Square, label: 'Rectangle' },
                                { m: 'circle' as DrawMode, Icon: Circle, label: 'Ellipse' },
                                { m: 'eraser' as DrawMode, Icon: Eraser, label: 'Eraser' },
                            ]).map(({ m, Icon, label }) => (
                                <Button
                                    key={m}
                                    variant={mode === m ? 'secondary' : 'ghost'}
                                    size="icon"
                                    className={cn("h-7 w-7 rounded-lg transition-all", mode === m && "ring-2 ring-primary ring-offset-1")}
                                    title={label}
                                    onClick={() => setMode(m)}
                                >
                                    <Icon className="h-3.5 w-3.5" />
                                </Button>
                            ))}

                            <div className="w-px h-5 bg-zinc-200 dark:bg-zinc-700 mx-1" />

                            {/* Color */}
                            <div className="relative">
                                <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg" title="Color" onClick={() => { setShowColorPicker(p => !p); setShowBrushPicker(false) }}>
                                    <span className="w-4 h-4 rounded-full border border-zinc-300" style={{ background: color }} />
                                </Button>
                                {showColorPicker && (
                                    <div className="absolute right-0 top-9 z-20 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl shadow-xl p-3 grid grid-cols-3 gap-2">
                                        {COLORS.map(c => (
                                            <button key={c} className={cn("w-6 h-6 rounded-full border-2 transition-transform hover:scale-110", c === color ? 'border-primary scale-110' : 'border-transparent', c === '#ffffff' && 'border-zinc-300')} style={{ background: c }} onClick={() => { setColor(c); setShowColorPicker(false) }} />
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Brush size */}
                            <div className="relative">
                                <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg" title="Brush Size" onClick={() => { setShowBrushPicker(p => !p); setShowColorPicker(false) }}>
                                    <span className="rounded-full bg-current" style={{ width: Math.min(brushSize, 14), height: Math.min(brushSize, 14) }} />
                                </Button>
                                {showBrushPicker && (
                                    <div className="absolute right-0 top-9 z-20 bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl shadow-xl p-3 flex flex-col gap-2 items-center">
                                        {BRUSH_SIZES.map(s => (
                                            <button key={s} className={cn("rounded-full bg-zinc-800 dark:bg-white transition-all hover:opacity-80 border-2", brushSize === s ? 'border-primary' : 'border-transparent')} style={{ width: s + 8, height: s + 8 }} onClick={() => { setBrushSize(s); setShowBrushPicker(false) }} />
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="w-px h-5 bg-zinc-200 dark:bg-zinc-700 mx-1" />

                            <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg text-blue-500 hover:text-blue-700 hover:bg-blue-50" title="Download" onClick={downloadCanvas}>
                                <Download className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50" title="Clear Canvas" onClick={clearCanvas}>
                                <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                    )}
                </div>

                {/* ── Canvas ─────────────────────────────────────────────── */}
                {!isMinimized && (
                    <div className="flex-1 relative overflow-hidden bg-white rounded-b-2xl" style={{ minHeight: 0 }}>
                        <canvas
                            ref={canvasRef}
                            className="absolute inset-0 w-full h-full touch-none"
                            style={{ cursor: mode === 'eraser' ? 'cell' : 'crosshair' }}
                            onPointerDown={startDraw}
                            onPointerMove={doDraw}
                            onPointerUp={stopDraw}
                            onPointerLeave={stopDraw}
                            onPointerCancel={stopDraw}
                        />
                        {/* Active color indicator at bottom edge */}
                        <div className="absolute bottom-0 left-0 right-0 h-[3px] rounded-b-2xl" style={{ background: color }} />
                    </div>
                )}
            </div>
        </div>,
        document.body
    )
}
